import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { OUTAGE_RELOAD_STORAGE_KEY } from '@shared/constants/storage-keys.const';
import { TRANSLATION_KEYS } from '@shared/constants/translation-keys.const';
import { WebStorageService } from '@shared/storage/web-storage.service';
import { ToastAction, ToastService, ToastVariant } from '@shared/services/toast.service';
import { SUPPRESS_ERROR_TOAST, SUPPRESS_ERROR_TOAST_EXCEPT_SERVICE_UNAVAILABLE } from './http-context-tokens';
import { OUTAGE_PAGE_RELOAD } from './outage-page-reload.token';
import { SERVICE_UNAVAILABLE_STATUSES } from './service-unavailable-statuses.const';

const DEDUPE_WINDOW_MS = 3000;

@Injectable({ providedIn: 'root' })
export class HttpErrorNotifierService {
  private readonly translocoService = inject(TranslocoService);
  private readonly toastService = inject(ToastService);
  private readonly storage = inject(WebStorageService);
  private readonly reloadPage = inject(OUTAGE_PAGE_RELOAD);

  private readonly lastNotifiedAt = new Map<string, number>();
  private outageToastId: number | null = null;

  public notify(request: HttpRequest<unknown>, error: HttpErrorResponse | Error): void {
    if (request.context.get(SUPPRESS_ERROR_TOAST)) return;

    const status = error instanceof HttpErrorResponse ? error.status : 0;
    const isOutage = SERVICE_UNAVAILABLE_STATUSES.includes(status);

    if (request.context.get(SUPPRESS_ERROR_TOAST_EXCEPT_SERVICE_UNAVAILABLE) && !isOutage) return;

    // Any answered status proves the API is reachable again, so a later outage gets to offer its
    // own reload instead of inheriting the spent one-shot marker from an earlier, resolved one.
    if (!isOutage) this.clearReloadMarker();

    if (this.isDuplicate(status, request)) return;

    if (isOutage) {
      this.showOutageToast();
      return;
    }

    this.toastService.show(this.translocoService.translate(this.userMessageKey(status)), this.variantFor(status));
  }

  // An outage fails many different urls at once, which the `status:url` dedupe window cannot
  // collapse. Since the toast is persistent, without this guard every failing url would stack one
  // more banner that never goes away on its own.
  private showOutageToast(): void {
    if (this.isOutageToastAlive()) return;

    this.outageToastId = this.toastService.show({
      title: this.translocoService.translate(TRANSLATION_KEYS.commonErrorHighDemandTitle),
      message: this.translocoService.translate(TRANSLATION_KEYS.commonErrorHighDemandMessage),
      variant: 'warning',
      persistent: true,
      action: this.reloadAction(),
    });
  }

  private isOutageToastAlive(): boolean {
    if (this.outageToastId === null) return false;

    const alive = this.toastService.toasts().some((toast) => toast.id === this.outageToastId && !toast.leaving);
    if (!alive) this.outageToastId = null;

    return alive;
  }

  // The action is offered at most once per browser session: reloading into a still-broken backend
  // would put the very same button back on screen, so the second offer is withheld rather than
  // shown as a control that refuses to act.
  private reloadAction(): ToastAction | undefined {
    if (this.readReloadMarker() !== null) return undefined;

    return {
      label: this.translocoService.translate(TRANSLATION_KEYS.commonErrorHighDemandReload),
      run: () => this.reloadOnce(),
    };
  }

  private reloadOnce(): void {
    try {
      if (this.storage.getItem(OUTAGE_RELOAD_STORAGE_KEY, 'session')) return;
      this.storage.setItem(OUTAGE_RELOAD_STORAGE_KEY, String(Date.now()), 'session');
    } catch {
      // Without sessionStorage there is no durable loop guard, so the reload is unsafe to perform.
      return;
    }

    this.reloadPage();
  }

  private readReloadMarker(): string | null {
    try {
      return this.storage.getItem(OUTAGE_RELOAD_STORAGE_KEY, 'session');
    } catch {
      return null;
    }
  }

  private clearReloadMarker(): void {
    try {
      this.storage.removeItem(OUTAGE_RELOAD_STORAGE_KEY, 'session');
    } catch {
      // Storage can be unavailable in privacy-restricted browser contexts.
    }
  }

  private isDuplicate(status: number, request: HttpRequest<unknown>): boolean {
    const key = `${status}:${request.urlWithParams}`;
    const now = Date.now();
    const lastAt = this.lastNotifiedAt.get(key);

    this.lastNotifiedAt.set(key, now);

    return lastAt !== undefined && now - lastAt < DEDUPE_WINDOW_MS;
  }

  private userMessageKey(status: number): string {
    if (status === 0) return TRANSLATION_KEYS.commonErrorNetwork;
    if (status === 401) return TRANSLATION_KEYS.commonErrorUnauthorized;
    if (status === 402) return TRANSLATION_KEYS.commonErrorPaymentRequired;
    if (status === 403) return TRANSLATION_KEYS.commonErrorForbidden;
    if (status === 404) return TRANSLATION_KEYS.commonErrorNotFound;
    if (status === 400 || status === 422) return TRANSLATION_KEYS.commonErrorValidation;
    if (status === 429) return TRANSLATION_KEYS.commonErrorTooManyRequests;
    return TRANSLATION_KEYS.commonErrorServer;
  }

  private variantFor(status: number): ToastVariant {
    if (status === 401) return 'neutral';
    return status >= 400 && status < 500 ? 'warning' : 'danger';
  }
}
